import { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  useTheme,
  Tab,
  Tabs,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  Snackbar,
  CircularProgress,
  alpha,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { format } from "date-fns";
import { useAuth } from "../../hooks/useAuth";
import AttendanceChart from "../../components/AttendanceChart";

// Empty State Component
const EmptyState = ({ type = "attendance" }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box
      sx={{
        height: type === "chart" ? "300px" : "400px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: alpha(colors.primary[400], 0.6),
        borderRadius: 2,
        p: 3,
        textAlign: "center",
      }}
    >
      <Typography
        variant="h5"
        color={colors.grey[100]}
        gutterBottom
        fontWeight="bold"
      >
        No {type === "chart" ? "Attendance Data" : "Records"} Yet
      </Typography>
      <Typography variant="body1" color={colors.grey[300]}>
        {type === "chart"
          ? "Driver attendance overview will appear here once trips are recorded."
          : "Start recording driver trips to see attendance records."}
      </Typography>
      <Typography
        variant="body2"
        color={colors.greenAccent[500]}
        sx={{ mt: 2 }}
      >
        Add new trips to begin tracking attendance
      </Typography>
    </Box>
  );
};

const Attendance = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user, api } = useAuth();

  // State
  const [attendance, setAttendance] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Fetch Data
  const fetchData = async () => {
    if (!user?._id) return;

    try {
      setLoading(true);
      const response = await api.get(`/attendance/${user._id}`);

      if (!response.data?.data?.attendance) {
        throw new Error("No attendance data found");
      }

      // Format attendance data
      const formattedAttendance = response.data.data.attendance.map((att) => ({
        _id: att._id,
        driver: {
          _id: att.driver._id,
          username: att.driver.username,
          image: att.driver.image || "/api/placeholder/40/40",
        },
        date: att.date,
        totalHours: parseFloat(att.totalHours) || 0,
        trips: att.trips || [],
      }));

      // Extract unique drivers
      const uniqueDrivers = Array.from(
        new Set(formattedAttendance.map((a) => a.driver._id))
      ).map((driverId) => {
        const driverData = formattedAttendance.find(
          (a) => a.driver._id === driverId
        );
        return driverData.driver;
      });

      // Format chart data
      const chartData = [
        {
          id: "Working Hours",
          data: Object.entries(
            formattedAttendance.reduce((acc, curr) => {
              const date = format(new Date(curr.date), "MM/dd");
              if (!acc[date]) {
                acc[date] = {
                  totalHours: 0,
                  tripCount: 0,
                  trips: [],
                };
              }
              acc[date].totalHours += parseFloat(curr.totalHours) || 0;
              acc[date].tripCount += curr.trips.length;
              acc[date].trips.push(...curr.trips);
              return acc;
            }, {})
          ).map(([date, data]) => ({
            x: date,
            y: data.totalHours.toFixed(2),
            tripCount: data.tripCount,
            trips: data.trips,
          })),
        },
      ];

      setAttendance(formattedAttendance);
      setDrivers(uniqueDrivers);
      setChartData(chartData);
      setError(null);
    } catch (error) {
      console.error("Error fetching attendance:", error);
      setError(
        error.response?.data?.message || "Failed to load attendance data"
      );
      setSnackbarOpen(true);
      setAttendance([]);
      setChartData([]);
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?._id) {
      fetchData();
    }
  }, [user]);

  // Prepare individual driver chart data
  const prepareDriverChartData = (driverId) => {
    const driverAttendance = attendance.filter(
      (a) => a.driver._id === driverId
    );
    return [
      {
        id: "Hours Worked",
        data: driverAttendance.map((a) => ({
          x: format(new Date(a.date), "MM/dd"),
          y: a.totalHours.toFixed(2),
          tripCount: a.trips.length,
        })),
      },
    ];
  };

  // DataGrid columns
  const columns = [
    {
      field: "driver",
      headerName: "Driver",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            src={params.row.driver?.image || "/api/placeholder/40/40"}
            alt={params.row.driver?.username || "Driver"}
          />
          <Typography>
            {params.row.driver?.username || "Unknown Driver"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      renderCell: (params) => {
        try {
          return format(new Date(params.row.date), "PP");
        } catch (error) {
          return "Invalid Date";
        }
      },
    },
    {
      field: "totalHours",
      headerName: "Hours Worked",
      flex: 1,
      renderCell: (params) => params.row.totalHours?.toFixed(2) || "0",
    },
    {
      field: "trips",
      headerName: "Trips",
      flex: 1,
      renderCell: (params) => params.row.trips?.length || 0,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setSelectedDriver(params.row);
            setDialogOpen(true);
          }}
        >
          View Details
        </Button>
      ),
    },
  ];
  // Loading State
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header title="ATTENDANCE" subtitle="Track Driver Attendance and Hours" />

      {/* Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>

      {/* Tabs */}
      <Tabs
        value={tabValue}
        onChange={(event, newValue) => setTabValue(newValue)}
        sx={{
          mb: 3,
          "& .MuiTabs-indicator": {
            backgroundColor: colors.blueAccent[500],
          },
          "& .MuiTab-root": {
            color: colors.grey[100],
            "&.Mui-selected": {
              color: colors.blueAccent[300],
              fontWeight: "bold",
            },
          },
        }}
      >
        <Tab label="Overview" />
        <Tab label="Detailed View" />
      </Tabs>

      {/* Overview Tab */}
      {tabValue === 0 ? (
        <Box>
          {/* Main Chart */}
          <Card
            sx={{
              backgroundColor: colors.primary[400],
              p: 3,
              mb: 3,
              height: "400px",
            }}
          >
            <Typography variant="h6" mb={2}>
              Average Working Hours Trend
            </Typography>
            {attendance.length > 0 ? (
              <AttendanceChart data={chartData} />
            ) : (
              <EmptyState type="chart" />
            )}
          </Card>

          {/* Driver Charts */}
          <Box
            display="grid"
            gridTemplateColumns={{
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(3, 1fr)",
            }}
            gap={3}
          >
            {drivers.length > 0 ? (
              drivers.map((driver) => (
                <Card
                  key={driver._id}
                  sx={{
                    backgroundColor: colors.primary[400],
                    p: 3,
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Avatar
                      src={driver.image || "/api/placeholder/56/56"}
                      sx={{ width: 56, height: 56 }}
                    />
                    <Box>
                      <Typography variant="h5">
                        {driver.username || "Unknown Driver"}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[300]}>
                        ID: {driver._id}
                      </Typography>
                    </Box>
                  </Box>
                  <Box height="200px">
                    <AttendanceChart
                      data={prepareDriverChartData(driver._id)}
                    />
                  </Box>
                </Card>
              ))
            ) : (
              <EmptyState />
            )}
          </Box>
        </Box>
      ) : (
        // Detailed View Tab
        <Card sx={{ backgroundColor: colors.primary[400], p: 3 }}>
          {attendance.length > 0 ? (
            <DataGrid
              rows={attendance}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 20]}
              autoHeight
              getRowId={(row) => row._id}
              sx={{
                "& .MuiDataGrid-cell": {
                  borderBottom: `1px solid ${colors.primary[300]}`,
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: colors.blueAccent[700],
                  borderBottom: "none",
                },
                "& .MuiDataGrid-virtualScroller": {
                  backgroundColor: colors.primary[400],
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: "none",
                  backgroundColor: colors.blueAccent[700],
                },
                "& .MuiCheckbox-root": {
                  color: `${colors.greenAccent[200]} !important`,
                },
              }}
            />
          ) : (
            <EmptyState />
          )}
        </Card>
      )}

      {/* Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedDriver?.driver?.username || "Driver"} - Attendance Details
        </DialogTitle>
        <DialogContent>
          {selectedDriver && (
            <Box p={2}>
              <Typography variant="h6" mb={2}>
                Date: {format(new Date(selectedDriver.date), "PP")}
              </Typography>
              <Typography mb={1}>
                Hours Worked: {selectedDriver.totalHours?.toFixed(2) || "0"}
              </Typography>
              <Typography mb={1}>
                Number of Trips: {selectedDriver.trips?.length || 0}
              </Typography>
              <Typography color={colors.grey[300]}>
                Driver ID: {selectedDriver.driver?._id || "N/A"}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Attendance;
